/**
 * tokenStorage — single facade for every auth-token read/write in the
 * renderer.
 *
 * - On the web, mirrors the existing behaviour: localStorage for "remember
 *   me", sessionStorage for tab-scoped impersonation handoff, plain reads.
 * - On the Electron desktop shell, the token lives ONLY in the OS-secure
 *   key/value store exposed by PlatformBridge (Step 6.3 + Step 13 "do NOT
 *   store auth tokens in plain localStorage on desktop").
 *
 * The public read API is synchronous on purpose so existing call sites
 * (authService.getToken, AuthContext initial state, axios request
 * interceptor, fetch() helpers) keep their shape. On desktop the cache is
 * hydrated asynchronously at boot via {@link hydrate}; the AuthProvider
 * awaits hydration before showing the login screen so the user never sees
 * a "signed out" flash.
 */

import { isDesktop, platformBridge } from './platformBridge';

const TRACKED_KEYS = ['token', 'platform_admin_token'] as const;
type TrackedKey = (typeof TRACKED_KEYS)[number];
type Bucket = 'local' | 'session';

class TokenStorage {
  private cache = new Map<string, string>();
  private hydrationPromise: Promise<void> | null = null;
  private hydrated = false;

  hydrate(): Promise<void> {
    if (this.hydrationPromise) return this.hydrationPromise;
    this.hydrationPromise = this.doHydrate();
    return this.hydrationPromise;
  }

  private async doHydrate(): Promise<void> {
    if (isDesktop) {
      // One-time migration: if a previous web-mode session left a key in
      // plain storage, lift it into the secure store and wipe the plain copy.
      for (const key of TRACKED_KEYS) {
        try {
          const secure = await platformBridge.secureStore.get(key);
          if (secure) {
            this.cache.set(key, secure);
            // Belt-and-braces wipe — Step 13 forbids plain-text tokens on
            // desktop even if hydrated from elsewhere.
            try {
              sessionStorage.removeItem(key);
            } catch {
              /* ignore */
            }
            try {
              localStorage.removeItem(key);
            } catch {
              /* ignore */
            }
            continue;
          }

          const legacy = this.readPlain(key);
          if (legacy) {
            await platformBridge.secureStore.set(key, legacy);
            this.cache.set(key, legacy);
          }
          try {
            sessionStorage.removeItem(key);
          } catch {
            /* ignore */
          }
          try {
            localStorage.removeItem(key);
          } catch {
            /* ignore */
          }
        } catch {
          /* secureStore unavailable — leave cache empty, the request
             interceptor will get a null token and the user re-logs in. */
        }
      }
    } else {
      for (const key of TRACKED_KEYS) {
        const v = this.readPlain(key);
        if (v) this.cache.set(key, v);
      }
    }
    this.hydrated = true;
  }

  private readPlain(key: string): string | null {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  isHydrated(): boolean {
    return this.hydrated;
  }

  /** Sync read — returns the last cached value. Call hydrate() at app boot. */
  get(key: TrackedKey): string | null {
    return this.cache.get(key) ?? null;
  }

  /**
   * Persist a token. On desktop always writes through safeStorage. On web,
   * the bucket parameter controls localStorage ("remember me", default) vs
   * sessionStorage (tab-scoped impersonation handoff).
   */
  async set(key: TrackedKey, value: string, bucket: Bucket = 'local'): Promise<void> {
    this.cache.set(key, value);
    if (isDesktop) {
      try {
        await platformBridge.secureStore.set(key, value);
      } catch {
        /* ignore — cache still holds it for the session */
      }
      return;
    }
    try {
      if (bucket === 'session') {
        sessionStorage.setItem(key, value);
      } else {
        localStorage.setItem(key, value);
      }
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * Mirroring write — used by the silent-refresh interceptor: writes back to
   * whichever bucket already holds the key so "remember me" semantics survive
   * a refresh, matching the original WebTokenStore behaviour.
   */
  async setMirroring(key: TrackedKey, value: string): Promise<void> {
    this.cache.set(key, value);
    if (isDesktop) {
      try {
        await platformBridge.secureStore.set(key, value);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      if (sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, value);
      } else {
        localStorage.setItem(key, value);
      }
    } catch {
      /* ignore */
    }
  }

  async remove(key: TrackedKey): Promise<void> {
    this.cache.delete(key);
    if (isDesktop) {
      try {
        await platformBridge.secureStore.delete(key);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

export const tokenStorage = new TokenStorage();

/** True only when running in Electron — re-exported for convenience. */
export { isDesktop };
